import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiShield } = FiIcons;

const Privacy = () => {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 pt-24">
      <div className="w-full max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-6 transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} />
            Back to Home
          </Link>

          <div className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-cyan-400/20 shadow-[0_0_50px_rgba(0,255,255,0.1)]">
            <div className="flex items-center gap-3 mb-6">
              <SafeIcon icon={FiShield} className="text-4xl text-cyan-400" />
              <h1 className="text-4xl font-bold text-white">Privacy Policy</h1>
            </div>

            <div className="space-y-6 text-gray-300">
              <section>
                <p className="mb-4">
                  <strong>Effective Date:</strong> January 1, 2025
                </p>
                <p>
                  At Vidroid, we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered video generation service.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">1. Information We Collect</h2>
                <p>
                  Vidroid is designed with privacy as a core principle. We collect minimal information necessary to provide our services:
                </p>

                <h3 className="text-lg font-semibold text-cyan-300 mt-4 mb-2">1.1 Information You Provide</h3>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>API Keys:</strong> Your Apimart and Kie.AI API keys are stored securely in your browser's localStorage and are never transmitted to our servers.</li>
                  <li><strong>Video Prompts:</strong> Text descriptions you provide for video generation are sent directly to the respective API providers (Apimart or Kie.AI) for processing.</li>
                  <li><strong>Contact Information:</strong> If you contact us via email or contact form, we collect your name and email address.</li>
                </ul>

                <h3 className="text-lg font-semibold text-cyan-300 mt-4 mb-2">1.2 Automatically Collected Information</h3>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>Video Metadata:</strong> Information about generated videos (creation time, status, settings) is stored locally in your browser.</li>
                  <li><strong>Usage Data:</strong> We may collect basic analytics data such as page views, browser type, and device information to improve our service.</li>
                  <li><strong>Log Data:</strong> Standard server logs may include IP addresses, browser information, and access times.</li>
                </ul>

                <h3 className="text-lg font-semibold text-cyan-300 mt-4 mb-2">1.3 Information We Do NOT Collect</h3>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>We do not collect or store your generated videos on our servers</li>
                  <li>We do not track your browsing behavior across other websites</li>
                  <li>We do not collect sensitive personal information such as financial data, health information, or government identifiers</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">2. API Key Storage</h2>
                <p>
                  Your API keys are stored securely in your browser's localStorage:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li><strong>Apimart API Key:</strong> Used for Sora 2, Veo 3.1, and MiniMax models</li>
                  <li><strong>Kie.AI API Key:</strong> Used for Grok Imagine, Hailuo 2.3, Wan 2.5, and Runway Aleph models</li>
                  <li>Both keys remain exclusively on your device</li>
                  <li>Keys are not transmitted to our servers or any third parties (except the respective API providers for video generation)</li>
                  <li>You can clear your keys at any time by clearing your browser data</li>
                  <li>Keys persist until you manually delete them or clear browser storage</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">3. Third-Party Services</h2>
                <p>
                  We use the following third-party services that may collect and process your information:
                </p>

                <h3 className="text-lg font-semibold text-cyan-300 mt-4 mb-2">3.1 Apimart</h3>
                <p>
                  For AI video generation with Sora 2, Veo 3.1, and MiniMax models, your prompts and API key are sent directly to Apimart. Apimart's use of your information is governed by their Privacy Policy.
                </p>

                <h3 className="text-lg font-semibold text-cyan-300 mt-4 mb-2">3.2 Kie.AI</h3>
                <p>
                  For AI video generation with Grok Imagine, Hailuo 2.3, Wan 2.5, and Runway Aleph models, your prompts and API key are sent directly to Kie.AI. Kie.AI's use of your information is governed by their Privacy Policy.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">4. Your Rights and Choices</h2>
                <p>
                  <strong>Local Data Management:</strong> You can delete your locally stored data at any time by clearing your browser's localStorage or using your browser's privacy settings. This includes:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>API keys (both Apimart and Kie.AI)</li>
                  <li>Video generation history</li>
                  <li>Preferences and settings</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">5. Security</h2>
                <p>
                  We implement reasonable security measures to protect your information:
                </p>
                <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                  <li>HTTPS encryption for all data transmission</li>
                  <li>Secure local storage of sensitive information (API keys)</li>
                  <li>Regular security assessments and updates</li>
                  <li>Limited access to any collected data</li>
                </ul>
                <p className="mt-3">
                  However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-cyan-400 mb-3">6. Contact Us</h2>
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <ul className="list-none ml-4 mt-2 space-y-2">
                  <li><strong>Email:</strong> <a href="mailto:fiverrings@gmail.com" className="text-cyan-400 hover:text-cyan-300">fiverrings@gmail.com</a></li>
                  <li><strong>Contact Form:</strong> <a href="https://fiverring.com/#/contact" target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:text-cyan-300">https://fiverring.com/#/contact</a></li>
                </ul>
              </section>

              <div className="mt-8 pt-6 border-t border-gray-700">
                <p className="text-sm text-gray-400">
                  <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  <strong>Effective Date:</strong> January 1, 2025
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Privacy;